import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { adminLogin, isAdminAuthenticated } from "@/lib/admin";
import { toast } from "sonner";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // If already logged in, jump straight to dashboard.
  useEffect(() => {
    isAdminAuthenticated().then((ok) => {
      if (ok) nav("/admin/dashboard");
    });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await adminLogin(email.trim().toLowerCase(), password);
      toast.success("Signed in");
      nav("/admin/dashboard");
    } catch (ex) {
      const msg = ex?.response?.data?.detail || ex.message || "Login failed";
      setErr(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans flex flex-col" data-testid="admin-login-page">
      <header className="border-b border-black/10">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="admin-login-back-home">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="h-4 w-px bg-black/20" />
            <Logo />
          </div>
          <div className="label-mono">Admin</div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="label-mono mb-6">
            <span className="inline-block w-6 h-px bg-black/40 align-middle mr-2" />
            Admin · Sign in
          </div>
          <h1 className="font-heading font-black tracking-tighter leading-[1.02] text-4xl">Analytics dashboard</h1>
          <p className="mt-3 text-sm text-neutral-600">
            Restricted area. Sign in with your admin credentials to view download analytics.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div>
              <div className="label-mono mb-1.5">Email</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field-input w-full"
                autoComplete="email"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <div className="label-mono mb-1.5">Password</div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field-input w-full"
                autoComplete="current-password"
                required
                data-testid="admin-password-input"
              />
            </div>
            {err && (
              <div className="text-xs text-red-600 border border-red-200 bg-red-50 px-3 py-2" data-testid="admin-login-error">{err}</div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center"
              data-testid="admin-login-submit"
            >
              <ShieldCheck size={14} weight="bold" />
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
